import { useLazyQuery, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner, { InlineSpinner } from "components/base/Spinner";
import { ProductImagesV2 } from "components/shared/ProductImages";
import { useModals } from "ModalProvider";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdExpandMore, MdRefresh } from "react-icons/md";
import CreatePoReport from "./CreatePoReport";
import TodoDateRangeSelect from "./components/TodoDateRangeSelect";
import { FETCH_QC_TODO_PO_LINES_BULK, FETCH_QC_TODO_PO_LIST } from "./graphql";

const EMPTY_LINE_STATE = { loading: false, lines: [], error: null };
const PO_TODO_LIST_VARIABLES = {
  days: 7,
  limit: 2000,
  forceRefresh: false,
};
const getPoListKey = (days, pos) =>
  `${days}:${pos.map((po) => po.id).join(",")}`;
const sortByPlannedDate = (a, b) =>
  (a.po.plannedDate || "9999-12-31").localeCompare(
    b.po.plannedDate || "9999-12-31",
  ) || a.po.id - b.po.id;

const PoDashboard = () => {
  const modal = useModals();
  const [days, setDays] = useState(PO_TODO_LIST_VARIABLES.days);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedPoIds, setExpandedPoIds] = useState(new Set());
  const [lineCache, setLineCache] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const bulkPrefetchedRef = useRef(null);

  const { loading, error, data, refetch } = useQuery(FETCH_QC_TODO_PO_LIST, {
    variables: { ...PO_TODO_LIST_VARIABLES, days },
  });

  const [fetchPoLinesBulk] = useLazyQuery(FETCH_QC_TODO_PO_LINES_BULK, {
    fetchPolicy: "network-only",
  });

  const poList = data?.qcTodoPoList ?? [];
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const filteredPoEntries = useMemo(() => {
    if (!normalizedSearchQuery) {
      return poList.map((po) => ({ po, filteredLines: null }));
    }

    return poList.reduce((acc, po) => {
      const poName = (po.name || "").toLowerCase();
      const vendorName = (po.vendorName || "").toLowerCase();
      const lines = lineCache[po.id]?.lines || [];

      if (
        poName.includes(normalizedSearchQuery) ||
        vendorName.includes(normalizedSearchQuery)
      ) {
        acc.push({ po, filteredLines: null });
        return acc;
      }

      const filteredLines = lines.filter((row) => {
        const productName = (row.product?.name || "").toLowerCase();
        const productNumber = (row.product?.number || "").toLowerCase();
        return (
          productName.includes(normalizedSearchQuery) ||
          productNumber.includes(normalizedSearchQuery)
        );
      });

      if (filteredLines.length > 0) {
        acc.push({ po, filteredLines });
      }
      return acc;
    }, []);
  }, [lineCache, normalizedSearchQuery, poList]);

  const loadPoLines = useCallback(
    (pos, forceRefresh = false) => {
      if (pos.length === 0) return Promise.resolve();
      const poListKey = getPoListKey(days, pos);
      const allLineIds = Array.from(
        new Set(pos.flatMap((po) => po.orderLineIds || [])),
      );
      if (allLineIds.length === 0) {
        bulkPrefetchedRef.current = poListKey;
        setLineCache((prev) => {
          const next = { ...prev };
          pos.forEach((po) => {
            next[po.id] = { loading: false, lines: [], error: null };
          });
          return next;
        });
        return Promise.resolve();
      }

      setLineCache((prev) => {
        const next = { ...prev };
        pos.forEach((po) => {
          next[po.id] = { loading: true, lines: [], error: null };
        });
        return next;
      });

      return fetchPoLinesBulk({
        variables: { lineIds: allLineIds, forceRefresh },
      })
        .then((res) => {
          const rows = res?.data?.qcTodoPoLinesBulk || [];
          const grouped = {};
          rows.forEach((row) => {
            if (!grouped[row.poId]) grouped[row.poId] = [];
            grouped[row.poId].push(row);
          });

          setLineCache((prev) => {
            const next = { ...prev };
            pos.forEach((po) => {
              next[po.id] = {
                loading: false,
                lines: grouped[po.id] || [],
                error: null,
              };
            });
            return next;
          });
          bulkPrefetchedRef.current = poListKey;
        })
        .catch((err) => {
          setLineCache((prev) => {
            const next = { ...prev };
            pos.forEach((po) => {
              next[po.id] = {
                loading: false,
                lines: [],
                error: err.message,
              };
            });
            return next;
          });
          bulkPrefetchedRef.current = poListKey;
        });
    },
    [days, fetchPoLinesBulk],
  );

  useEffect(() => {
    const poListKey = getPoListKey(days, poList);
    if (
      loading ||
      bulkPrefetchedRef.current === true ||
      bulkPrefetchedRef.current === poListKey ||
      poList.length === 0
    ) {
      return;
    }
    loadPoLines(poList);
  }, [days, loadPoLines, loading, poList]);

  const changeDays = (nextDays) => {
    if (nextDays === days) return;
    bulkPrefetchedRef.current = null;
    setLineCache({});
    setDays(nextDays);
  };

  const refreshPoTodo = async () => {
    if (refreshing) return;
    setRefreshing(true);
    bulkPrefetchedRef.current = true;
    setLineCache({});

    try {
      const res = await refetch({
        ...PO_TODO_LIST_VARIABLES,
        days,
        forceRefresh: true,
      });
      const refreshedPos = res?.data?.qcTodoPoList || [];
      await loadPoLines(refreshedPos, true);
    } finally {
      setRefreshing(false);
    }
  };

  const entriesWithState = useMemo(() => {
    return filteredPoEntries
      .map(({ po, filteredLines }) => {
        const lineState = lineCache[po.id] || EMPTY_LINE_STATE;
        const visibleLines = filteredLines || lineState.lines;
        return { po, lineState, visibleLines };
      })
      .filter(
        (entry) =>
          entry.lineState.loading ||
          entry.lineState.error ||
          entry.visibleLines.length > 0,
      )
      .sort(sortByPlannedDate);
  }, [filteredPoEntries, lineCache]);

  const togglePo = (poId) => {
    const next = new Set(expandedPoIds);
    if (next.has(poId)) {
      next.delete(poId);
      setExpandedPoIds(next);
      return;
    }
    next.add(poId);
    setExpandedPoIds(next);
  };

  if (loading && !refreshing) return <Spinner />;
  if (error) return <Errors error={error} />;

  return (
    <div className="p-6 md:h-[90vh] lg:h-[95vh] flex flex-col rounded-lg gap-4">
      <h2 className="hidden lg:block">To-do</h2>
      <div className="h-[80vh] md:h-[90vh] lg:h-full w-full flex flex-col overflow-hidden">
        <div className="sticky top-0 z-10 pb-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
            <SearchBar
              placeholder="PO、工厂、产品"
              initialQuery={searchQuery}
              onChange={setSearchQuery}
              containerClassName="w-full min-w-0 md:flex-1"
            />
            <div className="flex items-center gap-2 md:shrink-0">
              <TodoDateRangeSelect
                days={days}
                disabled={loading || refreshing}
                onChange={changeDays}
              />
              <button
                type="button"
                onClick={refreshPoTodo}
                disabled={loading || refreshing}
                className="h-[30px] w-[30px] shrink-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                title="重新从 Odoo 刷新 PO 待办"
                aria-label="重新从 Odoo 刷新 PO 待办"
              >
                <MdRefresh
                  className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 pr-3">
          {entriesWithState.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              暂无符合条件的 PO
            </div>
          ) : (
            entriesWithState.map((entry) => (
              <PoCard
                key={entry.po.id}
                po={entry.po}
                lineState={entry.lineState}
                visibleLines={entry.visibleLines}
                expanded={expandedPoIds.has(entry.po.id)}
                onToggle={() => togglePo(entry.po.id)}
                modal={modal}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const PoCard = ({ po, lineState, visibleLines, expanded, onToggle, modal }) => {
  const plannedDate = po.plannedDate
    ? moment(po.plannedDate).format("YYYY-MM-DD")
    : "?";
  const hasResolvedLineCount = !lineState.loading && !lineState.error;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800">
      <div
        className="px-3 md:px-4 py-3 flex items-start md:items-center cursor-pointer gap-2 md:gap-3"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="min-w-0 flex-1 md:flex md:items-center md:gap-3">
          <div className="text-left text-sm md:text-base text-gray-600 dark:text-gray-400 md:w-28 md:shrink-0">
            {plannedDate}
          </div>
          <div className="text-left text-base font-semibold text-gray-900 dark:text-gray-100 break-words md:w-36 md:shrink-0 md:truncate">
            {po.name}
          </div>
          <div className="mt-0.5 md:mt-0 text-left text-sm font-normal text-gray-500 dark:text-gray-400 break-words md:w-56 md:shrink-0 md:truncate">
            {po.vendorName || ""}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-1 md:gap-2 text-sm md:text-base text-gray-600 dark:text-gray-400">
          {lineState.loading ? (
            <InlineSpinner size={16} text={null} />
          ) : (
            hasResolvedLineCount && <span>{visibleLines.length} 项</span>
          )}
          <MdExpandMore
            className={`w-5 h-5 transform transition-transform duration-200 ${
              expanded ? "rotate-0 text-blue-500" : "-rotate-90 text-gray-400"
            }`}
          />
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {lineState.loading ? (
            <InlineSpinner size={16} text="正在加载明细..." />
          ) : lineState.error ? (
            <div className="text-red-500 text-sm">{lineState.error}</div>
          ) : visibleLines.length === 0 ? (
            <div className="text-sm text-gray-500">No product lines.</div>
          ) : (
            visibleLines.map((row) => (
              <PoRowCard key={row.id} row={row} modal={modal} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

const PoRowCard = ({ row, modal }) => {
  const product = row.product;
  const missingReasons = [];
  if (!row.poId || !row.poName) {
    missingReasons.push("缺少PO来源信息");
  }
  if (!product?.id) {
    missingReasons.push("缺少产品映射");
  }
  const blockReason = missingReasons.join("，");
  const canCreate = !!(row.poId && row.poName && product?.id);

  return (
    <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl">
      {product?.computedImages?.length ? (
        <ProductImagesV2 images={product.computedImages} />
      ) : null}

      <div className="mt-2 flex items-center gap-2 flex-wrap text-base font-semibold text-gray-900 dark:text-gray-100">
        <span className="font-bold">[{product?.number || "-"}]</span>
        <span>{product?.name || row.odooProductName}</span>
        <span className="font-bold">&times; {row.todoQty}</span>
      </div>

      <div className="mt-3">
        {canCreate ? (
          <Button
            onClick={() =>
              modal.present({
                title: "创建 PO 质检报告",
                center: true,
                children: (
                  <CreatePoReport
                    onCreated={modal.hide}
                    product={product}
                    qty={row.todoQty}
                    poOdooId={row.poId}
                    poLineOdooId={row.id}
                    po={row.poName}
                  />
                ),
              })
            }
          >
            + 创建质检报告
          </Button>
        ) : (
          <div className="text-sm text-red-600 font-bold">
            无法创建报告：{blockReason || "数据不完整"}
          </div>
        )}
      </div>
    </div>
  );
};

export default PoDashboard;
